import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, CheckCircle, ChevronDown, ChevronUp, CreditCard, DollarSign, ImageIcon,
  Minus, Package, Plus, Printer, RotateCcw, Search, ShoppingCart, Trash2, User, X,
} from 'lucide-react';
import api, { erpAPI } from '../../services/api';
import SearchableSelect from '../../components/admin/SearchableSelect';
import Toast from '../../components/admin/Toast';

const money = (v: any) => `\u09F3${Number(v || 0).toLocaleString('en-BD', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const rows = (p: any): any[] => Array.isArray(p) ? p : (p?.results || []);
const dateStr = (v: any) => v ? new Date(v).toLocaleDateString('en-BD') : '-';
const dt = (v: any) => v ? new Date(v).toLocaleString('en-BD') : '-';
const errorText = (e: any) => e?.response?.data?.detail || e?.message || 'Request failed';
const variantLabel = (v: any) => [v.product_name || v.product?.name_en, v.color, v.size].filter(Boolean).join(' - ') || v.sku || 'Item';

type ReturnItem = { sale_item: number; product_variant: number; label: string; unit_price: number; quantity: number; returned_qty: number; remaining: number; notes: string; reason: string };
type NewItem = { product_variant: number; label: string; sku: string; quantity: number; unit_price: number; stock: number };

export default function ReturnExchangeWorkspace({
  sale, onClose, onComplete, paymentMethods, variants, inventory, shopPrices,
}: {
  sale: any; onClose: () => void; onComplete: () => void;
  paymentMethods: any[]; variants: any[]; inventory: any[]; shopPrices: any[];
}) {
  const [step, setStep] = useState<'select' | 'replace' | 'settle' | 'confirm'>('select');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showInvoice, setShowInvoice] = useState<any>(null);

  // Return items state
  const [returnItems, setReturnItems] = useState<ReturnItem[]>([]);
  const [returnFlags, setReturnFlags] = useState<Record<number, boolean>>({});

  // Replacement items state (reuse POS patterns)
  const [productSearch, setProductSearch] = useState('');
  const [newItems, setNewItems] = useState<NewItem[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [variantQty, setVariantQty] = useState(1);

  // Settlement
  const [paymentMethod, setPaymentMethod] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  const searchRef = useRef<HTMLInputElement>(null);

  const paymentOpts = useMemo(() => paymentMethods.map((m: any) => ({ value: m.id, label: m.name })), [paymentMethods]);
  const refundPaymentOpts = useMemo(
    () => paymentMethods.filter((m: any) => Boolean(m.cash_bank_account)).map((m: any) => ({ value: m.id, label: m.name })),
    [paymentMethods]
  );
  const selectedRefund = useMemo(
    () => paymentMethods.find((m: any) => m.id === Number(paymentMethod)),
    [paymentMethods, paymentMethod]
  );

  // ── Initialize return items from sale ──
  useEffect(() => {
    if (sale?.items) {
      const items: ReturnItem[] = sale.items.map((i: any) => ({
        sale_item: i.id || i.sale_item,
        product_variant: i.product_variant,
        label: i.product_variant_name || variantLabel(i),
        unit_price: Number(i.unit_price || 0),
        quantity: Number(i.quantity || 0),
        returned_qty: 0,
        remaining: Number(i.quantity || 0),
        notes: '',
        reason: '',
      }));
      setReturnItems(items);
    }
  }, [sale]);

  const toggleReturnItem = (saleItemId: number) => {
    setReturnFlags(f => ({ ...f, [saleItemId]: !f[saleItemId] }));
    if (!returnFlags[saleItemId]) {
      setReturnItems(items => items.map(i => i.sale_item === saleItemId ? { ...i, returned_qty: i.remaining } : i));
    } else {
      setReturnItems(items => items.map(i => i.sale_item === saleItemId ? { ...i, returned_qty: 0 } : i));
    }
  };

  const updateReturnQty = (saleItemId: number, qty: number) => {
    const item = returnItems.find(i => i.sale_item === saleItemId);
    if (!item) return;
    const clamped = Math.max(0, Math.min(qty, item.remaining));
    setReturnItems(items => items.map(i => i.sale_item === saleItemId ? { ...i, returned_qty: clamped, reason: i.reason } : i));
    if (clamped > 0) setReturnFlags(f => ({ ...f, [saleItemId]: true }));
    else setReturnFlags(f => ({ ...f, [saleItemId]: false }));
  };

  const updateReturnReason = (saleItemId: number, reason: string) => {
    setReturnItems(items => items.map(i => i.sale_item === saleItemId ? { ...i, reason } : i));
  };

  // ── Computed values ──
  const selectedReturnItems = useMemo(() => returnItems.filter(i => i.returned_qty > 0), [returnItems]);
  const returnedTotal = useMemo(() => selectedReturnItems.reduce((s, i) => s + i.returned_qty * i.unit_price, 0), [selectedReturnItems]);
  const newItemsTotal = useMemo(() => newItems.reduce((s, i) => s + i.quantity * i.unit_price, 0), [newItems]);
  const difference = useMemo(() => newItemsTotal - returnedTotal, [newItemsTotal, returnedTotal]);
  const transactionType = useMemo(() => newItems.length === 0 ? 'Return' : 'Exchange', [newItems.length]);

  // ── Product search (reuse POS patterns) ──
  const shopId = sale?.shop;
  const filteredProducts = useMemo(() => {
    if (!productSearch) return [];
    const q = productSearch.toLowerCase();
    return variants.filter((v: any) => {
      const name = (v.product_name || v.product?.name_en || '').toLowerCase();
      const sku = (v.sku || '').toLowerCase();
      if (!name.includes(q) && !sku.includes(q)) return false;
      const stock = inventory.find((s: any) => s.location === shopId && s.product_variant === v.id);
      return stock && stock.available_quantity > 0;
    }).slice(0, 20);
  }, [variants, productSearch, inventory, shopId]);

  const product = useMemo(() => selectedProduct ? variants.find((v: any) => v.id === selectedProduct) : null, [selectedProduct, variants]);
  const productVariants = useMemo(() => {
    if (!product) return [];
    const pid = product.product || product.product_id;
    return variants.filter((v: any) => (v.product || v.product_id) === pid);
  }, [product, variants]);
  const colors = useMemo(() => [...new Set(productVariants.map((v: any) => v.color).filter(Boolean))] as string[], [productVariants]);
  const sizes = useMemo(() => {
    const filtered = selectedColor ? productVariants.filter((v: any) => v.color === selectedColor) : productVariants;
    return [...new Set(filtered.map((v: any) => v.size).filter(Boolean))] as string[];
  }, [productVariants, selectedColor]);
  useEffect(() => { if (colors.length === 1 && !selectedColor) setSelectedColor(colors[0]); }, [colors, selectedColor]);
  useEffect(() => { if (sizes.length === 1 && !selectedSize) setSelectedSize(sizes[0]); }, [sizes, selectedSize]);

  const matchingVariants = useMemo(() => {
    if (!shopId || !selectedProduct) return [];
    return productVariants.filter((v: any) => {
      if (selectedColor && v.color !== selectedColor) return false;
      if (selectedSize && v.size !== selectedSize) return false;
      const stock = inventory.find((s: any) => s.location === shopId && s.product_variant === v.id);
      return stock && stock.available_quantity > 0;
    }).map((v: any) => {
      const stock = inventory.find((s: any) => s.location === shopId && s.product_variant === v.id);
      const sp = shopPrices.find((p: any) => p.shop === shopId && p.product_variant === v.id);
      return { ...v, available: stock?.available_quantity || 0, unit_price: Number(sp?.normal_price || 0) };
    });
  }, [productVariants, shopId, selectedProduct, selectedColor, selectedSize, inventory, shopPrices]);

  const addNewItem = (v: any, qty = variantQty) => {
    setNewItems(items => {
      const existing = items.find(i => i.product_variant === v.id);
      if (existing) return items.map(i => i.product_variant === v.id ? { ...i, quantity: Math.min(i.quantity + qty, v.available) } : i);
      return [...items, { product_variant: v.id, label: variantLabel(v), sku: v.sku || '', quantity: qty, unit_price: v.unit_price, stock: v.available }];
    });
    setSelectedProduct(null); setSelectedColor(''); setSelectedSize('');
  };

  const removeNewItem = (id: number) => setNewItems(items => items.filter(i => i.product_variant !== id));
  const updateNewItemQty = (id: number, qty: number) => setNewItems(items => items.map(i => i.product_variant === id ? { ...i, quantity: Math.max(1, Math.min(qty, i.stock)) } : i));

  // ── Validation ──
  const canProceedToReplace = useMemo(() => selectedReturnItems.length > 0, [selectedReturnItems]);
  const canProceedToSettle = useMemo(() => selectedReturnItems.length > 0, [selectedReturnItems]);
  const canComplete = useMemo(() => {
    if (!selectedReturnItems.length) return false;
    if (difference > 0 && !paymentMethod) return false;
    if (difference < 0 && !(paymentMethod && selectedRefund?.cash_bank_account)) return false;
    return true;
  }, [selectedReturnItems, difference, paymentMethod, selectedRefund]);

  // ── Complete ──
  const completeExchange = async () => {
    if (!canComplete) return;
    setSaving(true);
    try {
      const payload: any = {
        original_sale: sale.id,
        return_items: selectedReturnItems.map(i => ({ sale_item: i.sale_item, quantity: i.returned_qty })),
        new_items: newItems.map(i => ({ product_variant: i.product_variant, quantity: i.quantity })),
        notes: notes || '',
      };

      // Build payments — the selected payment account carries its cash/bank account
      const payments: any[] = [];
      if (difference > 0) {
        payments.push({
          payment_type: 'PAYMENT',
          method: Number(paymentMethod),
          cash_bank_account: Number(selectedRefund?.cash_bank_account) || undefined,
          amount: Math.abs(difference),
          reference: reference || '',
        });
      } else if (difference < 0) {
        payments.push({
          payment_type: 'REFUND',
          method: Number(paymentMethod),
          cash_bank_account: Number(selectedRefund?.cash_bank_account),
          amount: Math.abs(difference),
          reference: reference || '',
        });
      }
      if (payments.length) payload.payments = payments;

      const resp = await erpAPI.create('pos-exchanges', payload);
      const exchange = resp.data;
      setShowInvoice({
        ...exchange,
        return_items: selectedReturnItems,
        new_items: newItems,
        customer_name: sale.customer_name || 'Walk-in',
        sale_number: sale.sale_number,
        difference,
      });
      setMessage({ type: 'success', text: `Exchange completed! ${exchange.exchange_number || ''}` });
    } catch (e) { setMessage({ type: 'error', text: errorText(e) }); }
    finally { setSaving(false); }
  };

  const printReceipt = (data: any) => {
    const w = window.open('', '_blank', 'width=800,height=600');
    if (!w) return;
    const items = [...(data.return_items || []).map((i: any) => `Return: ${i.label} × ${i.returned_qty} = ${money(i.returned_qty * i.unit_price)}`),
      ...(data.new_items || []).map((i: any) => `New: ${i.label} × ${i.quantity} = ${money(i.quantity * i.unit_price)}`)].join('<br/>');
    w.document.write(`<html><head><title>Exchange ${data.exchange_number}</title>
      <style>body{font-family:sans-serif;padding:30px;max-width:400px;margin:auto}
      h2{font-size:16px;margin-bottom:4px;color:#1e293b}
      .meta{font-size:12px;color:#64748b;margin-bottom:16px}</style></head><body>
      <h2>Exchange Receipt</h2>
      <div class="meta">${data.exchange_number || ''}<br>${dt(new Date())}<br>${data.customer_name || ''}</div>
      <div class="meta">Original Invoice: ${data.sale_number || ''}</div>
      <div>${items}</div>
      <div class="meta" style="margin-top:16px;font-weight:700">
        ${data.difference > 0 ? 'Amount Due: ' + money(data.difference) : data.difference < 0 ? 'Refund: ' + money(Math.abs(data.difference)) : 'No balance due'}
      </div></body></html>`);
    w.document.close(); w.print();
  };

  // ── KBD Style ──
  const kbd: React.CSSProperties = { padding: '1px 5px', borderRadius: 3, border: '1px solid #cbd5e1', background: '#f1f5f9', fontSize: '.6rem', fontWeight: 700, fontFamily: 'monospace', color: '#475569' };

  return <div className="erp-page" style={{ maxWidth: 960, margin: '0 auto' }}>
    <Toast show={Boolean(message)} message={message?.text || ''} type={message?.type || 'info'} onClose={() => setMessage(null)} />

    {/* Header */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
      <div>
        <h2 style={{ margin: 0, fontSize: '1.2rem' }}>
          {transactionType === 'Return' ? 'Sale Return' : 'Exchange'} — {sale.sale_number}
        </h2>
        <div style={{ fontSize: '.78rem', color: '#64748b', marginTop: 2 }}>
          {sale.customer_name || 'Walk-in'} · {dateStr(sale.completed_at)}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ fontSize: '.72rem', color: '#94a3b8' }}>
          Step {step === 'select' ? '1' : step === 'replace' ? '2' : step === 'settle' ? '3' : '4'} of 4
        </div>
        <button onClick={onClose} className="acct-btn acct-btn-secondary">Cancel</button>
      </div>
    </div>

    {/* Step indicators */}
    <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
      {['Select Items', 'Replacement', 'Settlement', 'Confirm'].map((label, idx) => {
        const phases = ['select', 'replace', 'settle', 'confirm'];
        const current = phases.indexOf(step);
        return <div key={label} style={{ flex: 1, padding: '6px 8px', borderRadius: 6, textAlign: 'center', fontSize: '.72rem', fontWeight: 700,
          background: idx <= current ? '#eef2ff' : '#f1f5f9', color: idx <= current ? '#4f46e5' : '#94a3b8' }}>
          {label}
        </div>;
      })}
    </div>

    {/* ──── STEP 1: Select Return Items ──── */}
    {step === 'select' && <div>
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: 14, marginBottom: 12 }}>
        <h3 style={{ margin: '0 0 10px', fontSize: '.85rem', color: '#334155' }}>Original Sale Summary</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, fontSize: '.78rem' }}>
          <div><span style={{ color: '#64748b' }}>Invoice</span><br/><strong>{sale.sale_number}</strong></div>
          <div><span style={{ color: '#64748b' }}>Customer</span><br/><strong>{sale.customer_name || 'Walk-in'}</strong></div>
          <div><span style={{ color: '#64748b' }}>Date</span><br/><strong>{dateStr(sale.completed_at)}</strong></div>
          <div><span style={{ color: '#64748b' }}>Total</span><br/><strong style={{ color: '#059669' }}>{money(sale.total_amount)}</strong></div>
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid #e2e8f0', fontSize: '.82rem', fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
          <span>Items Sold</span>
          <span style={{ color: '#64748b', fontWeight: 400 }}>{selectedReturnItems.length} selected · {money(returnedTotal)}</span>
        </div>
        {returnItems.map(item => <div key={item.sale_item} style={{ padding: '8px 14px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10 }}>
          <input type="checkbox" checked={!!returnFlags[item.sale_item]} onChange={() => toggleReturnItem(item.sale_item)}
            style={{ width: 16, height: 16, accentColor: '#4f46e5', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: '.8rem' }}>{item.label}</div>
            <div style={{ fontSize: '.7rem', color: '#64748b' }}>{money(item.unit_price)} each · Sold: {item.quantity} · Remaining: {item.remaining}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button disabled={!returnFlags[item.sale_item]} onClick={() => updateReturnQty(item.sale_item, item.returned_qty - 1)}
              style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 4, padding: '2px 5px', cursor: 'pointer', opacity: returnFlags[item.sale_item] ? 1 : .4 }}><Minus size={11} /></button>
            <input type="number" value={item.returned_qty} min={0} max={item.remaining}
              onChange={e => updateReturnQty(item.sale_item, Number(e.target.value))}
              disabled={!returnFlags[item.sale_item]}
              style={{ width: 36, textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px', fontSize: '.78rem' }} />
            <button disabled={!returnFlags[item.sale_item]} onClick={() => updateReturnQty(item.sale_item, item.returned_qty + 1)}
              style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 4, padding: '2px 5px', cursor: 'pointer', opacity: returnFlags[item.sale_item] ? 1 : .4 }}><Plus size={11} /></button>
          </div>
          <input value={item.reason} onChange={e => updateReturnReason(item.sale_item, e.target.value)}
            placeholder="Reason" style={{ width: 100, border: '1px solid #e2e8f0', borderRadius: 4, padding: '4px 6px', fontSize: '.72rem' }} />
        </div>)}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button onClick={onClose} className="acct-btn acct-btn-secondary">Cancel</button>
        <button onClick={() => setStep('replace')} disabled={!canProceedToReplace}
          className="acct-btn acct-btn-primary" style={{ opacity: canProceedToReplace ? 1 : .5, cursor: canProceedToReplace ? 'pointer' : 'not-allowed' }}>
          Next — {returnedTotal > 0 ? money(returnedTotal) : 'Select items'} {newItems.length > 0 ? '(Exchange)' : '(Return)'}
        </button>
      </div>
    </div>}

    {/* ──── STEP 2: Replacement Items ──── */}
    {step === 'replace' && <div>
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '10px 14px', marginBottom: 12 }}>
        <h3 style={{ margin: '0 0 8px', fontSize: '.85rem' }}>Add Replacement Items</h3>
        <p style={{ margin: '0 0 10px', fontSize: '.75rem', color: '#64748b' }}>
          Search and add replacement products. Leave empty for a <strong>Return Only</strong>.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px' }}>
          <Search size={14} style={{ color: '#94a3b8' }} />
          <input ref={searchRef} value={productSearch} onChange={e => { setProductSearch(e.target.value); setSelectedProduct(null); }}
            placeholder="F2 — Search product name or SKU..."
            style={{ border: 0, background: 'transparent', flex: 1, outline: 'none', fontSize: '.8rem' }} />
          {productSearch && <X size={14} style={{ color: '#94a3b8', cursor: 'pointer' }} onClick={() => { setProductSearch(''); searchRef.current?.focus(); }} />}
        </div>
      </div>

      {/* Product / Variant selector */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: 10, marginBottom: 12, maxHeight: 260, overflowY: 'auto' }}>
        {!selectedProduct ? (
          !productSearch ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: '.8rem' }}>
              <Package size={32} style={{ opacity: .2, marginBottom: 6 }} />
              <p>Search for products above</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>No products match your search.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {filteredProducts.map((v: any) => (
                <div key={v.id} onClick={() => { setSelectedProduct(v.id); setSelectedColor(''); setSelectedSize(''); setProductSearch(''); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', cursor: 'pointer', borderRadius: 6, transition: 'background .1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '.78rem' }}>{variantLabel(v)}</div>
                    <div style={{ fontSize: '.68rem', color: '#64748b' }}>{v.sku || ''}</div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <div>
            <button onClick={() => { setSelectedProduct(null); setSelectedColor(''); setSelectedSize(''); }}
              style={{ border: 0, background: '#fee2e2', color: '#991b1b', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontWeight: 600, fontSize: '.65rem', marginBottom: 8 }}>
              <X size={12} style={{ verticalAlign: 'middle' }} /> Back
            </button>
            <div style={{ fontWeight: 600, fontSize: '.82rem', marginBottom: 8 }}>{variantLabel(product)}</div>
            {colors.length > 1 && <div style={{ marginBottom: 6 }}>
              <label style={{ fontSize: '.65rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', display: 'block', marginBottom: 4 }}>Color</label>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {colors.map(c => <button key={c} onClick={() => { setSelectedColor(c === selectedColor ? '' : c); setSelectedSize(''); }}
                  style={{ padding: '3px 10px', borderRadius: 5, border: selectedColor === c ? '2px solid #4f46e5' : '1px solid #e2e8f0', background: selectedColor === c ? '#eef2ff' : '#fff', fontWeight: 600, fontSize: '.72rem', cursor: 'pointer' }}>{c}</button>)}
              </div>
            </div>}
            {sizes.length > 1 && <div style={{ marginBottom: 6 }}>
              <label style={{ fontSize: '.65rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', display: 'block', marginBottom: 4 }}>Size</label>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {sizes.map(s => <button key={s} onClick={() => setSelectedSize(s === selectedSize ? '' : s)}
                  style={{ padding: '3px 10px', borderRadius: 5, border: selectedSize === s ? '2px solid #4f46e5' : '1px solid #e2e8f0', background: selectedSize === s ? '#eef2ff' : '#fff', fontWeight: 600, fontSize: '.72rem', cursor: 'pointer' }}>{s}</button>)}
              </div>
            </div>}
            {matchingVariants.map((v: any) => (
              <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', background: '#f8fafc', borderRadius: 6, marginTop: 4 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '.78rem' }}>{variantLabel(v)}</div>
                  <div style={{ fontSize: '.68rem', color: '#64748b' }}>{v.available} in stock · {money(v.unit_price)}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button onClick={() => setVariantQty(q => Math.max(1, q - 1))} style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 4, padding: '2px 5px', cursor: 'pointer' }}><Minus size={11} /></button>
                  <input type="number" value={variantQty} onChange={e => setVariantQty(Math.min(Math.max(1, Number(e.target.value) || 1), v.available))}
                    style={{ width: 30, textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px', fontSize: '.78rem' }} />
                  <button onClick={() => setVariantQty(q => Math.min(q + 1, v.available))} style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 4, padding: '2px 5px', cursor: 'pointer' }}><Plus size={11} /></button>
                </div>
                <button onClick={() => addNewItem(v, variantQty)} style={{ border: 0, background: '#4f46e5', color: '#fff', borderRadius: 5, padding: '4px 8px', cursor: 'pointer', fontWeight: 600, fontSize: '.68rem', whiteSpace: 'nowrap' }}>+ Add</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New items list */}
      {newItems.length > 0 && <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: 10, marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: '.78rem', marginBottom: 6 }}>Replacement Items ({money(newItemsTotal)})</div>
        {newItems.map(item => <div key={item.product_variant} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
          <div style={{ flex: 1, minWidth: 0, fontSize: '.78rem', fontWeight: 500 }}>{item.label}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button onClick={() => updateNewItemQty(item.product_variant, item.quantity - 1)} style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 4, padding: '1px 4px', cursor: 'pointer' }}><Minus size={10} /></button>
            <span style={{ fontSize: '.78rem', fontWeight: 600, width: 20, textAlign: 'center' }}>{item.quantity}</span>
            <button onClick={() => updateNewItemQty(item.product_variant, item.quantity + 1)} style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 4, padding: '1px 4px', cursor: 'pointer' }}><Plus size={10} /></button>
          </div>
          <span style={{ fontSize: '.75rem', fontWeight: 600, width: 70, textAlign: 'right' }}>{money(item.quantity * item.unit_price)}</span>
          <button onClick={() => removeNewItem(item.product_variant)} style={{ border: 0, background: 'transparent', color: '#94a3b8', cursor: 'pointer', padding: 2 }}><Trash2 size={12} /></button>
        </div>)}
      </div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 8 }}>
        <div style={{ fontSize: '.82rem', color: '#64748b' }}>
          Return: <strong style={{ color: '#dc2626' }}>{money(returnedTotal)}</strong>
          {newItems.length > 0 && <> · New: <strong style={{ color: '#059669' }}>{money(newItemsTotal)}</strong> · Diff: <strong style={{ color: difference > 0 ? '#dc2626' : '#059669' }}>{money(Math.abs(difference))}</strong></>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setStep('select')} className="acct-btn acct-btn-secondary">Back</button>
          <button onClick={() => setStep('settle')} disabled={!canProceedToSettle}
            className="acct-btn acct-btn-primary" style={{ opacity: canProceedToSettle ? 1 : .5, cursor: canProceedToSettle ? 'pointer' : 'not-allowed' }}>
            Next — {transactionType}
          </button>
        </div>
      </div>
    </div>}

    {/* ──── STEP 3: Settlement ──── */}
    {step === 'settle' && <div>
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: 14, marginBottom: 12 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: '.85rem' }}>Settlement Summary</h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ background: '#f8fafc', borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', marginBottom: 8 }}>Returned Items</div>
            {selectedReturnItems.map(i => <div key={i.sale_item} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.78rem', marginBottom: 4 }}>
              <span>{i.label} × {i.returned_qty}</span><strong>{money(i.returned_qty * i.unit_price)}</strong>
            </div>)}
            <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 6, paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
              <span>Total Returned</span><span style={{ color: '#059669' }}>{money(returnedTotal)}</span>
            </div>
          </div>

          <div style={{ background: '#f8fafc', borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', marginBottom: 8 }}>Replacement Items</div>
            {newItems.length === 0 ? (
              <div style={{ fontSize: '.78rem', color: '#94a3b8' }}>No replacement items (Return Only)</div>
            ) : newItems.map(i => <div key={i.product_variant} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.78rem', marginBottom: 4 }}>
              <span>{i.label} × {i.quantity}</span><strong>{money(i.quantity * i.unit_price)}</strong>
            </div>)}
            <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 6, paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
              <span>Total New</span><span>{money(newItemsTotal)}</span>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 14, padding: 12, background: difference === 0 ? '#f0fdf4' : difference > 0 ? '#fef2f2' : '#eff6ff', borderRadius: 8, textAlign: 'center' }}>
          <div style={{ fontSize: '.82rem', fontWeight: 700 }}>
            {difference > 0
              ? <>Customer Pays: <span style={{ color: '#dc2626', fontSize: '1rem' }}>{money(difference)}</span></>
              : difference < 0
              ? <>Customer Refund: <span style={{ color: '#059669', fontSize: '1rem' }}>{money(Math.abs(difference))}</span></>
              : <>No Balance Due <CheckCircle size={14} style={{ verticalAlign: 'middle', color: '#059669' }} /></>}
          </div>
        </div>

        {difference > 0 && <div style={{ marginTop: 12 }}>
          <div className="acct-form-group"><label className="acct-form-label">Payment Account *</label>
            <SearchableSelect value={paymentMethod} onChange={setPaymentMethod} placeholder="Select" options={paymentOpts} /></div>
          <div className="acct-form-group"><label className="acct-form-label">Reference</label>
            <input className="acct-form-input" value={reference} onChange={e => setReference(e.target.value)} placeholder="Optional ref" /></div>
        </div>}

        {difference < 0 && <div style={{ marginTop: 12 }}>
          <div className="acct-form-group"><label className="acct-form-label">Refund Payment Account *</label>
            <SearchableSelect value={paymentMethod} onChange={setPaymentMethod} placeholder="Select refund account" options={refundPaymentOpts} /></div>
          <div className="acct-form-group"><label className="acct-form-label">Reference</label>
            <input className="acct-form-input" value={reference} onChange={e => setReference(e.target.value)} placeholder="Refund ref" /></div>
        </div>}

        <div className="acct-form-group" style={{ marginTop: 10 }}><label className="acct-form-label">Notes</label>
          <textarea className="acct-form-textarea" value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Exchange / return notes" rows={2} /></div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <button onClick={() => setStep('replace')} className="acct-btn acct-btn-secondary">Back</button>
        <button onClick={() => setStep('confirm')} disabled={!canComplete}
          className="acct-btn acct-btn-primary" style={{ opacity: canComplete ? 1 : .5, cursor: canComplete ? 'pointer' : 'not-allowed' }}>
          Review Transaction
        </button>
      </div>
    </div>}

    {/* ──── STEP 4: Confirm ──── */}
    {step === 'confirm' && <div>
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: 14, marginBottom: 12 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: '.85rem' }}>Confirm {transactionType}</h3>

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', marginBottom: 6 }}>Returning to Stock</div>
          {selectedReturnItems.map(i => <div key={i.sale_item} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.78rem', padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
            <span>{i.label} × {i.returned_qty}</span><strong style={{ color: '#059669' }}>{money(i.returned_qty * i.unit_price)}</strong>
          </div>)}
        </div>

        {newItems.length > 0 && <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', marginBottom: 6 }}>Deducting from Stock</div>
          {newItems.map(i => <div key={i.product_variant} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.78rem', padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
            <span>{i.label} × {i.quantity}</span><strong style={{ color: '#dc2626' }}>{money(i.quantity * i.unit_price)}</strong>
          </div>)}
        </div>}

        <div style={{ padding: 12, background: difference === 0 ? '#f0fdf4' : difference > 0 ? '#fef2f2' : '#eff6ff', borderRadius: 8, textAlign: 'center', marginTop: 8 }}>
          <div style={{ fontSize: '.9rem', fontWeight: 700 }}>
            {difference > 0
              ? <>Collect: <span style={{ color: '#dc2626' }}>{money(difference)}</span></>
              : difference < 0
              ? <>Refund: <span style={{ color: '#059669' }}>{money(Math.abs(difference))}</span></>
              : <>No Payment Due</>}
          </div>
        </div>

        <div style={{ marginTop: 12, fontSize: '.75rem', color: '#64748b', background: '#f8fafc', borderRadius: 8, padding: 10 }}>
          This will update inventory and post accounting entries. The original sale remains unchanged.
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button onClick={() => setStep('settle')} className="acct-btn acct-btn-secondary">Back</button>
        <button onClick={completeExchange} disabled={saving}
          className="acct-btn acct-btn-success" style={{ opacity: saving ? .5 : 1, cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          {saving ? 'Processing...' : <><CheckCircle size={16} /> Complete {transactionType}</>}
        </button>
      </div>
    </div>}

    {/* ──── Success Modal ──── */}
    {showInvoice && <div className="acct-modal-overlay" onClick={() => setShowInvoice(null)}>
      <div className="acct-modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="acct-modal-header">
          <h3 className="acct-modal-title">{transactionType} Completed</h3>
          <button className="acct-modal-close" onClick={() => setShowInvoice(null)}><X size={16} /></button>
        </div>
        <div className="acct-modal-body" style={{ textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 52, borderRadius: '50%', background: '#d1fae5', color: '#059669', marginBottom: 10 }}>
            <CheckCircle size={26} />
          </div>
          <h2 style={{ margin: '0 0 4px', fontSize: '1.1rem' }}>{showInvoice.exchange_number || 'Completed'}</h2>
          <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: '.82rem' }}>Original Invoice: {sale.sale_number}</p>

          <div style={{ background: '#f8fafc', borderRadius: 8, padding: 12, marginBottom: 12, textAlign: 'left' }}>
            <div style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', marginBottom: 6 }}>
              {transactionType} Summary
            </div>
            <div style={{ fontSize: '.78rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}><span>Customer</span><strong>{showInvoice.customer_name || 'Walk-in'}</strong></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}><span>Returned</span><strong style={{ color: '#059669' }}>{money(returnedTotal)}</strong></div>
              {newItems.length > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}><span>Replacement</span><strong>{money(newItemsTotal)}</strong></div>}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderTop: '1px solid #e2e8f0', marginTop: 4 }}>
                <span style={{ fontWeight: 700 }}>{difference > 0 ? 'Amount Due' : difference < 0 ? 'Refund' : 'Balance'}</span>
                <strong style={{ color: difference > 0 ? '#dc2626' : difference < 0 ? '#059669' : '#64748b', fontSize: '.9rem' }}>
                  {difference > 0 ? money(difference) : difference < 0 ? money(Math.abs(difference)) : '—'}
                </strong>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => printReceipt(showInvoice)} className="acct-btn" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Printer size={14} /> Print Receipt
            </button>
            <button onClick={() => { setShowInvoice(null); onComplete(); }} className="acct-btn acct-btn-success" style={{ flex: 1 }}>
              Done
            </button>
          </div>
        </div>
      </div>
    </div>}
  </div>;
}
